import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog';
import { Search, Loader2, Eye, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { SortableHeader, useSort } from '../../components/ui/sortable-header';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientLoansPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loans, setLoans] = useState([]);
  const [totalLoans, setTotalLoans] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const { sortConfig, handleSort, sortItems } = useSort('purchase_date', 'desc');
  const limit = 20;

  useEffect(() => {
    fetchLoans();
  }, [token, page, statusFilter]);

  const fetchLoans = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/api/client/loans?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoans(response.data.items || []);
      setTotalLoans(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching loans:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLoanDetail = async (loanId) => {
    try {
      const response = await axios.get(`${API_URL}/api/client/loans/${loanId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedLoan(response.data);
      setDetailOpen(true);
    } catch (err) {
      console.error('Error fetching loan detail:', err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatPercent = (rate) => {
    return `${((rate || 0) * 100).toFixed(2)}%`;
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      settled: 'bg-blue-100 text-blue-800',
      written_off: 'bg-red-100 text-red-800'
    };
    return variants[status] || variants.active;
  };

  const filteredLoans = sortItems(
    loans.filter(loan =>
      String(loan.loan_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      loan.deal_type?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const totalPages = Math.ceil(totalLoans / limit);

  return (
    <div className="space-y-6" data-testid="client-loans-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-2xl font-bold text-[#212529] font-['Manrope']">My Loans</h1>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by Loan ID or Deal Type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="loan-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="settled">Settled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Loans Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0D3B66]" />
            </div>
          ) : filteredLoans.length === 0 ? (
            <div className="text-center py-12 text-[#6C757D]">
              No loans found
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader label="Loan Number" sortKey="loan_number" sortConfig={sortConfig} onSort={handleSort} />
                      <SortableHeader label="Deal Type" sortKey="deal_type" sortConfig={sortConfig} onSort={handleSort} />
                      <SortableHeader label="Purchase Date" sortKey="purchase_date" sortConfig={sortConfig} onSort={handleSort} />
                      <SortableHeader label="Settlement Date" sortKey="final_settlement_date" sortConfig={sortConfig} onSort={handleSort} />
                      <SortableHeader label="Original Amount" sortKey="original_amount" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
                      <SortableHeader label="Outstanding" sortKey="outstanding" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
                      <SortableHeader label="Rate" sortKey="interest_rate" sortConfig={sortConfig} onSort={handleSort} className="text-center" />
                      <SortableHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={handleSort} className="text-center" />
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLoans.map((loan) => (
                      <TableRow key={loan.loan_number}>
                        <TableCell className="font-medium">{loan.loan_number}</TableCell>
                        <TableCell>{loan.deal_type}</TableCell>
                        <TableCell>{formatDate(loan.purchase_date)}</TableCell>
                        <TableCell>{formatDate(loan.final_settlement_date || loan.repurchase_date)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(loan.original_amount)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(loan.outstanding)}</TableCell>
                        <TableCell className="text-center">{formatPercent(loan.interest_rate)}</TableCell>
                        <TableCell className="text-center">
                          <Badge className={getStatusBadge(loan.status)}>
                            {loan.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => fetchLoanDetail(loan.loan_number)}
                            title={`View details for Loan ${loan.loan_number}`}
                            data-testid={`view-loan-${loan.loan_number}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-[#6C757D]">
                    Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalLoans)} of {totalLoans}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">Page {page} of {totalPages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Loan Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Loan Details: Loan {selectedLoan?.loan_number}</DialogTitle>
          </DialogHeader>
          {selectedLoan && (
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div>
                <p className="text-sm text-[#6C757D]">Deal Type</p>
                <p className="font-medium">{selectedLoan.deal_type}</p>
              </div>
              <div>
                <p className="text-sm text-[#6C757D]">Status</p>
                <Badge className={getStatusBadge(selectedLoan.status)}>
                  {selectedLoan.status}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-[#6C757D]">Purchase Date</p>
                <p className="font-medium">{formatDate(selectedLoan.purchase_date)}</p>
              </div>
              <div>
                <p className="text-sm text-[#6C757D]">Final Settlement Date</p>
                <p className="font-medium">{formatDate(selectedLoan.final_settlement_date || selectedLoan.repurchase_date)}</p>
              </div>
              <div>
                <p className="text-sm text-[#6C757D]">Total Loan Amount</p>
                <p className="font-medium">{formatCurrency(selectedLoan.loan_amount_zar)}</p>
              </div>
              <div>
                <p className="text-sm text-[#6C757D]">Outstanding Balance</p>
                <p className="font-medium text-lg">{formatCurrency(selectedLoan.closing_balance)}</p>
              </div>
              <div>
                <p className="text-sm text-[#6C757D]">Interest Rate</p>
                <p className="font-medium">{formatPercent(selectedLoan.interest_rate)} p.a.</p>
              </div>
              <div>
                <p className="text-sm text-[#6C757D]">Cumulative Interest</p>
                <p className="font-medium">{formatCurrency(selectedLoan.interest_cumulative)}</p>
              </div>
              {selectedLoan.seller && (
                <div className="col-span-2">
                  <p className="text-sm text-[#6C757D]">Seller</p>
                  <p className="font-medium">{selectedLoan.seller}</p>
                </div>
              )}
              {/* No tranche visibility for clients */}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientLoansPage;
